import { Color, Element } from '@thive/canvas';
import { Flow } from '@thive/fddm-types';
import { MathUtils, Vector2, Vector3 } from 'three';

import { FLOW_STROKE_WIDTH, Z_INDEX_MAP } from '@/modules/canvas/constants/elements';
import { elementFactory } from '@/modules/canvas/factories';
import { NumUtils } from '@/modules/common/utils';
import {
  getAreaAttachPoint,
  getFlowLineColor,
  getGroupAttachPoint,
} from '@/modules/flowsV2/helpers';
import { getRenderableFlowEndpointId } from '@/modules/flowsV2/flowEndpoint';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import {
  areasToFlatCollection,
  isRackingArea,
  isSingularStationArea,
} from '@/modules/floorplanDesign/helpers';
import { FDDM_TAG, INTERACTIVITY_OFF } from '@/modules/floorplanDesign/constants';
import { modifyColor } from '@/modules/floorplanDesign/mappers/colors';
import { isManualArea } from '@/modules/floorplanDesign/helpers/typeguards';
import { logger } from '@common/logging/logger';

const FLOW_LOAD_LABEL_FONT_SIZE = 1300;
const FLOW_LOAD_LABEL_HEIGHT = 0.45;
const FLOW_LOAD_LABEL_CHARACTER_WIDTH = 0.12;
const FLOW_LOAD_LABEL_HORIZONTAL_PADDING = 0.18;
const FLOW_LOAD_LABEL_FLOW_PADDING = 0.25;
const FLOW_LOAD_LABEL_MINIMUM_WIDTH = 0.001;

export const getFlowCanvasElementIds = (flowId: string): string[] => [
  flowId,
  `${flowId}-loads-per-hour-group`,
];

const getReadableLabelRotation = (angle: number): number => {
  let rotation = MathUtils.radToDeg(angle);

  if (rotation > 90) {
    rotation -= 180;
  }

  if (rotation < -90) {
    rotation += 180;
  }

  return rotation;
};

const createFlowLoadLabelElements = (flow: Flow, pointA: Vector2, pointB: Vector2): Element[] => {
  const hourlyOrderCount = flow.hourlyOrderCounts[0]; // for now pick first, potentially needs to be updated later
  if (hourlyOrderCount === undefined) {
    return [];
  }

  const labelText = `${hourlyOrderCount}`;

  const calculatedLabelWidth =
    labelText.length * FLOW_LOAD_LABEL_CHARACTER_WIDTH + FLOW_LOAD_LABEL_HORIZONTAL_PADDING * 2;

  const labelWidth = Math.max(calculatedLabelWidth, FLOW_LOAD_LABEL_MINIMUM_WIDTH);

  const flowLength = NumUtils.mmToMeters(pointA.distanceTo(pointB));
  const requiredFlowLength = labelWidth + FLOW_LOAD_LABEL_FLOW_PADDING * 2;
  const visible = flowLength >= requiredFlowLength;

  const center = pointA.clone().add(pointB).multiplyScalar(0.5);
  const centerInMeters = new Vector2(NumUtils.mmToMeters(center.x), NumUtils.mmToMeters(center.y));

  const angle = Math.atan2(pointB.y - pointA.y, pointB.x - pointA.x);
  const rotation = getReadableLabelRotation(angle);
  const groupId = `${flow.id}-loads-per-hour-group`;
  const textId = `${flow.id}-loads-per-hour-text`;

  return [
    elementFactory.buildNoneElement({
      id: groupId,
      name: groupId,
      position: new Vector3(
        centerInMeters.x,
        centerInMeters.y,
        Z_INDEX_MAP[FDDM_TAG.FLOW_LOADS_PER_HOUR],
      ),
      rotation: new Vector3(0, 0, rotation),
      interactivity: INTERACTIVITY_OFF,
      tags: [FDDM_TAG.FLOW_LOADS_PER_HOUR, FDDM_TAG.LAYER_FLOWS],
    }),
    elementFactory.buildTextElement({
      id: textId,
      name: textId,
      parentId: groupId,
      text: labelText,
      position: new Vector3(),
      fontSize: FLOW_LOAD_LABEL_FONT_SIZE,
      width: NumUtils.metersToMmRounded(labelWidth),
      height: NumUtils.metersToMmRounded(FLOW_LOAD_LABEL_HEIGHT),
      wrapping: false,
      color: '#222222',
      backgroundColor: '#00000000',
      horizontalAlignment: 'Center',
      verticalAlignment: 'Center',
      interactivity: INTERACTIVITY_OFF,
      tags: [FDDM_TAG.FLOW_LOADS_PER_HOUR, FDDM_TAG.LAYER_FLOWS],
      visible,
      autoScale: false,
    }),
  ];
};

export const createFlowElement = (flow: Flow, highlight: boolean): Element[] => {
  const { getAreaById, getAreasByIds, getFlowStopById } = useFloorplanDesignStore.getState();

  const getEndpointPoint = (id: string, otherPoint?: Vector2): Vector2 | undefined => {
    const resolvedId = getRenderableFlowEndpointId(id);
    const area = getAreaById(resolvedId);

    if (area) {
      if (!isSingularStationArea(area) && !isRackingArea(area) && !isManualArea(area)) {
        return undefined;
      }

      return getAreaAttachPoint(area.rectangle, otherPoint);
    }

    const group = getFlowStopById(resolvedId);
    if (!group) return undefined;

    const areas = areasToFlatCollection(getAreasByIds(group.areaIds));

    return getGroupAttachPoint(areas, otherPoint);
  };

  const sourceCenter = getEndpointPoint(flow.sourceAreaOrFlowStopId);
  const destinationCenter = getEndpointPoint(flow.destinationAreaOrFlowStopId);

  if (!sourceCenter) {
    logger.error(`Invalid flow source: ${flow.sourceAreaOrFlowStopId}`);
    return [];
  }

  if (!destinationCenter) {
    logger.error(`Invalid flow destination: ${flow.destinationAreaOrFlowStopId}`);
    return [];
  }

  const pointA = getEndpointPoint(flow.sourceAreaOrFlowStopId, destinationCenter) ?? sourceCenter;

  const pointB =
    getEndpointPoint(flow.destinationAreaOrFlowStopId, sourceCenter) ?? destinationCenter;

  const baseColor = getFlowLineColor(flow.type);
  const strokeColor = highlight ? baseColor : modifyColor(baseColor, false, '80');

  return [
    {
      id: flow.id,
      name: flow.id,
      type: 'Arrow',
      interactivity: {
        ...INTERACTIVITY_OFF,
        selectable: true,
        clickable: true,
        hoverable: true,
      },
      pointA: new Vector3(pointA.x, pointA.y, Z_INDEX_MAP[FDDM_TAG.FLOW]),
      pointB: new Vector3(pointB.x, pointB.y, Z_INDEX_MAP[FDDM_TAG.FLOW]),
      stroke: Color.fromHex(strokeColor),
      strokeWidth: FLOW_STROKE_WIDTH,
      autoStrokeWidth: true,
      tags: [FDDM_TAG.FLOW, FDDM_TAG.LAYER_FLOWS],
    },
    ...createFlowLoadLabelElements(flow, pointA, pointB),
  ];
};

export const flowMapper = (flows: Flow[], highlightedFlows: string[]): Element[] => {
  const highlightedFlowIds = new Set(highlightedFlows);

  return flows.flatMap((flow) => createFlowElement(flow, highlightedFlowIds.has(flow.id)));
};
